import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { ChevronDown, ChevronRight } from 'lucide-react'
import type { ApiEventRow } from '../../lib/api'
import { Badge, Panel, Spinner, ErrorState, Json } from '../../components/ui'
import { fmtDateTime } from '../../lib/format'
import { useEvents } from './useAgents'

export function EventsTab({ active }: { active: boolean }) {
  const { data, isLoading, isError } = useEvents(active)

  if (isLoading) return <Spinner />
  if (isError) return <ErrorState message="Couldn't load events." />

  const events = data ?? []
  const queued = events.filter((e) => !e.acked_at).length

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-gray-700">
        The event log is the queue the orchestrator consumes. Each event is picked up and, where
        relevant, delegated to an agent run. Queued events are awaiting the orchestrator; completed
        ones have already been acknowledged.
      </div>
      <Panel title={`Event log — ${queued} queued · ${events.length} total`}>
        {events.length === 0 ? (
          <p className="px-1 py-6 text-center text-sm text-text-muted">No events yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-text-muted">
                  <th className="px-3 py-2 font-medium">When</th>
                  <th className="px-3 py-2 font-medium">Event</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Thesis</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <EventRow key={e.id} event={e} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  )
}

function EventRow({ event: e }: { event: ApiEventRow }) {
  const [open, setOpen] = useState(false)

  return (
    <>
      <tr
        onClick={() => setOpen(!open)}
        className="cursor-pointer border-b border-gray-200 last:border-0 hover:bg-gray-50"
      >
        <td className="px-3 py-2">
          <span className="flex items-center gap-2">
            {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            {fmtDateTime(e.created_at)}
          </span>
        </td>
        <td className="px-3 py-2">
          <Badge tone="indigo">{e.event_type}</Badge>
        </td>
        <td className="px-3 py-2">
          <Badge tone={e.acked_at ? 'green' : 'amber'}>{e.acked_at ? 'completed' : 'queued'}</Badge>
        </td>
        <td className="px-3 py-2" onClick={(ev) => ev.stopPropagation()}>
          {e.thesis_id ? (
            <Link
              to="/theses/$thesisId"
              params={{ thesisId: e.thesis_id }}
              className="text-slate-700 hover:underline"
            >
              {e.thesis_name || e.thesis_id.slice(0, 8)}
            </Link>
          ) : (
            <span className="text-text-muted">—</span>
          )}
        </td>
      </tr>
      {open && (
        <tr className="border-b border-gray-200 last:border-0 bg-gray-50/50">
          <td colSpan={4} className="px-3 py-3">
            <div className="flex flex-col gap-3">
              {e.run_id && (
                <div className="text-xs text-text-muted">
                  Run:{' '}
                  <Link
                    to="/agents/runs/$runId"
                    params={{ runId: e.run_id }}
                    className="font-mono text-slate-700 hover:underline"
                  >
                    {e.run_id.slice(0, 8)}
                  </Link>
                </div>
              )}
              <Json value={e.payload} />
            </div>
          </td>
        </tr>
      )}
    </>
  )
}
